import React, { useState, useMemo } from "react";
import { Container } from "react-bootstrap";
import { motion, AnimatePresence } from "motion/react";
import { projects } from "../../data/portfolioData";
import ProjectCard from "./ProjectCards";
import ProjectModal from "./ProjectModal";
import { FaMobileAlt, FaLaptopCode, FaServer, FaThLarge, FaSearch } from "react-icons/fa";

function Projects() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);

  const filterTabs = [
    { id: "all", label: "All Projects", icon: <FaThLarge />, count: projects.length },
    { id: "mobile", label: "Mobile Apps (React Native)", icon: <FaMobileAlt />, count: projects.filter(p => p.category === "mobile").length },
    { id: "web", label: "Web & Full Stack", icon: <FaLaptopCode />, count: projects.filter(p => p.category === "web").length },
    { id: "backend", label: "Backend & Systems", icon: <FaServer />, count: projects.filter(p => p.category === "backend").length },
  ];

  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      const matchesCategory = activeFilter === "all" || project.category === activeFilter;
      const matchesSearch =
        project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.subtitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [activeFilter, searchQuery]);

  return (
    <section className="projects-page-section" id="projects">
      <Container>
        {/* Section Header */}
        <motion.div
          className="section-head text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="badge-pill mb-3">
            <span>✨ Application & Systems Portfolio</span>
          </div>
          <h1 className="page-title">
            Featured <span className="gradient-text">Applications</span> & Systems
          </h1>
          <p className="page-lead">
            Production mobile applications shipped to the Apple App Store & Google Play Store, along with full-stack web platforms, developer tooling, and backend microservices.
          </p>
        </motion.div>

        {/* Filter Toolbar & Search */}
        <motion.div
          className="portfolio-toolbar"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <div className="filter-tabs-scroll">
            <div className="filter-tabs-wrapper">
              {filterTabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`filter-tab-btn ${activeFilter === tab.id ? "active" : ""}`}
                  onClick={() => setActiveFilter(tab.id)}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  <span className="tab-label">{tab.label}</span>
                  <span className="tab-counter">{tab.count}</span>
                  {activeFilter === tab.id && (
                    <motion.div
                      className="active-pill-bg"
                      layoutId="activeFilterPill"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="search-box-wrapper">
            <FaSearch className="search-input-icon" />
            <input
              type="text"
              placeholder="Filter by keyword (e.g. React Native, OAuth, Maps)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="portfolio-search-input"
            />
            {searchQuery && (
              <button className="clear-search-btn" onClick={() => setSearchQuery("")}>
                ✕
              </button>
            )}
          </div>
        </motion.div>

        {/* Projects Grid */}
        <div className="projects-grid-layout">
          <AnimatePresence mode="popLayout">
            {filteredProjects.length > 0 ? (
              filteredProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onSelectProject={setSelectedProject}
                />
              ))
            ) : (
              <motion.div
                className="no-results-box text-center py-5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <h3>No projects found matching "{searchQuery}"</h3>
                <p>Try searching for a different keyword or reset filters.</p>
                <button
                  className="btn-modern secondary"
                  onClick={() => {
                    setActiveFilter("all");
                    setSearchQuery("");
                  }}
                >
                  Reset All Filters
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Container>

      {/* Deep-Dive Project Modal */}
      <ProjectModal
        project={selectedProject}
        isOpen={Boolean(selectedProject)}
        onClose={() => setSelectedProject(null)}
      />
    </section>
  );
}

export default Projects;
